import {
    RemoteMcpClient as SdkRemoteMcpClient,
    type McpRemoteTool,
    type McpRemoteToolCallResult,
    type RemoteMcpRequestObservation,
} from '@secureblob/sdk/remoteMcpClient'
import type { NormalizedAgentMcpServerConfig } from '../agentTypes'
import { recordBrowserHttpErrorResponse } from '@secureblob/sdk/browserHttpErrors'
import {
    recordMcpToolCall,
    recordSqlQueryDebugSnapshot,
    recordLaneHeadHistory,
} from '@secureblob/sdk/browserMcpTraffic'
import {
    appendRuntimeEvent,
} from '../../testing/runtimeEventTrace'

export type { McpRemoteTool, McpRemoteToolCallResult } from '@secureblob/sdk/remoteMcpClient'

function isSecureBlobMcpProxyUrl(url: string): boolean {
    return url.startsWith('/api/mcp-proxy')
}

function readStringField(value: Record<string, unknown> | null, key: string): string | null {
    return value && typeof value[key] === 'string'
        ? value[key] as string
        : null
}

function readNumberField(value: Record<string, unknown> | null, key: string): number | null {
    const candidate = value?.[key]
    return typeof candidate === 'number' && Number.isFinite(candidate)
        ? candidate
        : null
}

function readBooleanField(value: Record<string, unknown> | null, key: string): boolean | null {
    const candidate = value?.[key]
    return typeof candidate === 'boolean'
        ? candidate
        : null
}

function resolveSqlRequestScope(args: Record<string, unknown> | null): {
    workspaceRef: string
    path: string
} {
    if (!args) {
        return { workspaceRef: '', path: '' }
    }

    const workspaceRef = typeof args.workspaceRef === 'string'
        ? args.workspaceRef
        : ''
    const path = typeof args.path === 'string'
        ? args.path
        : ''

    if (workspaceRef || path) {
        return { workspaceRef, path }
    }

    const vpath = typeof args.vpath === 'string' ? args.vpath : null
    if (!vpath) {
        return { workspaceRef: '', path: '' }
    }

    const separatorIndex = vpath.indexOf(':')
    if (separatorIndex <= 0 || separatorIndex === vpath.length - 1) {
        return { workspaceRef: '', path: '' }
    }

    return {
        workspaceRef: vpath.slice(0, separatorIndex),
        path: vpath.slice(separatorIndex + 1),
    }
}

export class RemoteMcpClient {
    private readonly client: SdkRemoteMcpClient

    constructor(
        serverName: string,
        server: NormalizedAgentMcpServerConfig,
        fetchImpl: typeof fetch = fetch,
    ) {
        this.client = new SdkRemoteMcpClient(serverName, {
            url: server.url,
            headers: server.headers,
            fetch: fetchImpl,
        }, {
            onHttpResponse: ({ url, method, response }) => {
                if (isSecureBlobMcpProxyUrl(url)) {
                    recordBrowserHttpErrorResponse(response, { url, method })
                }
            },
            onRequestComplete: (observation) => {
                recordSqlToolSnapshot(observation)
                // §255: inline sqlite-write-lane signal directly — no intermediate wrapper
                if (
                    observation.method === 'tools/call'
                    && !observation.isError
                    && observation.resultData !== null
                    && observation.toolResult !== null
                ) {
                    const toolName = typeof observation.params?.name === 'string'
                        ? observation.params.name
                        : null
                    if (toolName === 'secureblob_sql_execute') {
                        const toolArgs = observation.params?.arguments && typeof observation.params.arguments === 'object'
                            ? observation.params.arguments as Record<string, unknown>
                            : null
                        const requestScope = resolveSqlRequestScope(toolArgs)
                        const provenance = observation.toolResult.structuredContent
                        const resultData = observation.resultData
                        const path = readStringField(provenance, 'path')
                            ?? readStringField(resultData, 'path')
                            ?? requestScope.path
                        const lane = readStringField(provenance, 'lane')
                            ?? readStringField(resultData, 'lane')
                        if (path || lane) {
                            appendRuntimeEvent(
                                'sqlite-write-lane',
                                {
                                    workspaceRef: readStringField(provenance, 'workspaceRef')
                                        ?? readStringField(resultData, 'workspaceRef')
                                        ?? requestScope.workspaceRef,
                                    path,
                                    source: readStringField(provenance, 'source')
                                        ?? readStringField(resultData, 'source'),
                                    lane,
                                    transactionMode: readStringField(provenance, 'transactionMode')
                                        ?? readStringField(resultData, 'transactionMode'),
                                    changedPageCount: readNumberField(provenance, 'changedPageCount')
                                        ?? readNumberField(resultData, 'changedPageCount'),
                                    pageCount: readNumberField(provenance, 'pageCount')
                                        ?? readNumberField(resultData, 'pageCount'),
                                    changed: readBooleanField(provenance, 'changed')
                                        ?? readBooleanField(resultData, 'changed'),
                                    rowsAffected: readNumberField(provenance, 'rowsAffected')
                                        ?? readNumberField(resultData, 'rowsAffected'),
                                    lastInsertRowid: readNumberField(provenance, 'lastInsertRowid')
                                        ?? readNumberField(resultData, 'lastInsertRowid'),
                                    durationMs: readNumberField(provenance, 'durationMs')
                                        ?? readNumberField(resultData, 'durationMs')
                                        ?? observation.durationMs,
                                },
                                'engine',
                            )
                        }

                        // §307 §2: record lane head history on successful write commit.
                        // Surface 'mcp-client' because this write was initiated by the MCP client
                        // (server-side agent or tool calling secureblob_sql_execute).
                        const workspaceRefValue = readStringField(provenance, 'workspaceRef')
                            ?? readStringField(resultData, 'workspaceRef')
                            ?? requestScope.workspaceRef
                        const pathValue = readStringField(provenance, 'path')
                            ?? readStringField(resultData, 'path')
                            ?? requestScope.path
                        const laneValue = readStringField(provenance, 'lane')
                            ?? readStringField(resultData, 'lane')
                        const pageCountValue = readNumberField(provenance, 'pageCount')
                            ?? readNumberField(resultData, 'pageCount')
                        const txMode = readStringField(provenance, 'transactionMode')
                            ?? readStringField(resultData, 'transactionMode')
                        if (laneValue != null && pageCountValue != null) {
                            recordLaneHeadHistory(
                                'mcp-client',
                                laneValue,
                                pageCountValue,
                                0, // commitCount: not available from MCP tool result
                                (txMode as 'sequential' | 'concurrent' | 'exclusive') ?? 'concurrent',
                                {
                                    workspaceRef: workspaceRefValue,
                                    path: pathValue,
                                },
                            )
                        }
                    }
                }
                recordMcpToolCall({
                    method: observation.method,
                    connectionLabel: observation.connectionLabel,
                    connectionType: observation.connectionType,
                    callId: observation.callId,
                    capturedAt: observation.capturedAt,
                    durationMs: observation.durationMs,
                    isError: observation.isError,
                    redactedParams: observation.redactedParams,
                    redactedResult: observation.redactedResult,
                })
            },
        })
    }

    async listTools(): Promise<McpRemoteTool[]> {
        return this.client.listTools()
    }

    async callTool(name: string, args: Record<string, unknown>): Promise<McpRemoteToolCallResult> {
        return this.client.callTool(name, args)
    }
}

function recordSqlToolSnapshot(observation: RemoteMcpRequestObservation): void {
    if (
        observation.method !== 'tools/call'
        || observation.isError
        || observation.resultData === null
        || observation.toolResult === null
    ) {
        return
    }

    const toolName = typeof observation.params?.name === 'string'
        ? observation.params.name
        : null
    if (
        toolName !== 'secureblob_sql_query'
        && toolName !== 'secureblob_sql_schema'
        && toolName !== 'secureblob_sql_execute'
    ) {
        return
    }

    const toolArgs = observation.params?.arguments && typeof observation.params.arguments === 'object'
        ? observation.params.arguments as Record<string, unknown>
        : null
    const requestScope = resolveSqlRequestScope(toolArgs)
    const provenance = observation.toolResult.structuredContent
    const resultData = observation.resultData

    recordSqlQueryDebugSnapshot(
        observation.callId,
        observation.capturedAt,
        toolName,
        toolName !== 'secureblob_sql_schema' && toolArgs
            ? (typeof toolArgs.sql === 'string' ? toolArgs.sql : null)
            : null,
        toolArgs,
        readStringField(provenance, 'workspaceRef')
            ?? readStringField(resultData, 'workspaceRef')
            ?? requestScope.workspaceRef,
        readStringField(provenance, 'path')
            ?? readStringField(resultData, 'path')
            ?? requestScope.path,
        readStringField(provenance, 'source')
            ?? readStringField(resultData, 'source')
            ?? (observation.toolResult.errorText ? 'unknown' : ''),
        readStringField(provenance, 'lane')
            ?? readStringField(resultData, 'lane'),
        toolName === 'secureblob_sql_query'
            ? readNumberField(provenance, 'rowCount')
                ?? readNumberField(resultData, 'rowCount')
            : null,
        toolName === 'secureblob_sql_execute'
            ? readNumberField(provenance, 'rowsAffected')
                ?? readNumberField(resultData, 'rowsAffected')
            : null,
        toolName === 'secureblob_sql_execute'
            ? readNumberField(provenance, 'lastInsertRowid')
                ?? readNumberField(resultData, 'lastInsertRowid')
            : null,
        toolName === 'secureblob_sql_execute'
            ? readStringField(provenance, 'transactionMode')
                ?? readStringField(resultData, 'transactionMode')
            : null,
        toolName === 'secureblob_sql_execute'
            ? readBooleanField(provenance, 'changed')
                ?? readBooleanField(resultData, 'changed')
            : null,
        toolName === 'secureblob_sql_execute'
            ? readNumberField(provenance, 'changedPageCount')
                ?? readNumberField(resultData, 'changedPageCount')
            : null,
        toolName === 'secureblob_sql_execute'
            ? readNumberField(provenance, 'pageCount')
                ?? readNumberField(resultData, 'pageCount')
            : null,
        observation.durationMs,
        observation.toolResult.errorText,
    )

}
